import React, { useMemo } from 'react';
import { Box, Container, Header, Spinner } from '@amzn/awsui-components-core';
import { EnhancedAISummaryBox } from 'src/components/generic-components/ai-summary-components/EnhancedAISummaryBox';
import { useMultiLeaderHeadcount } from '../useConnectionsData';
import { ConnectionsScorecard } from '../types';
import { buildCanaryInsightsPayload, toRawDataContent, CanaryFreetextTopic } from './insightsPayload';
import { LeaderTier } from './playbookUtils';
import styles from './AIInsightsSummary.module.scss';

/**
 * AI Insights Summary card for the Canary Insights tab.
 *
 * Assembles the connection x headcount x attrition payload (insightsPayload) for the
 * focus leader + directs over the selected period, then streams an AI briefing via the
 * existing FinSuite events-API summarizer (EnhancedAISummaryBox -> finsum-publisher/page).
 *
 * The backend routes pageType "Canary Insights" to the `connections_analyst` MODEL_CONFIG
 * (Step 3). Until that routing lands, the generic business_analyst prompt is used.
 */
interface AIInsightsSummaryProps {
  focusLeader: { login: string; name: string; tier: LeaderTier };
  /** Focus leader + directs (login + display name), same array passed to HeadcountInsights. */
  leaders: Array<{ login: string; name: string }>;
  scorecards: ConnectionsScorecard[];
  period: { label: string; startDate: string | null; cadence: string | null };
  /** Optional aggregated free-text topics (no verbatim). */
  freetextTopics?: CanaryFreetextTopic[];
  /**
   * False when the scorecards/leaders passed do NOT yet belong to `focusLeader`
   * (a stale render during a leader switch, before the drill-down refetch settles).
   * Defaults to true. When false, we suppress payload assembly so the summarizer is
   * never fired with the previously-selected leader's directs.
   */
  leaderMatches?: boolean;
  /** True while the upstream drill-down is refetching for the new leader (show spinner). */
  dataLoading?: boolean;
}

const PAGE_TYPE = 'Canary Insights';

export const AIInsightsSummary: React.FC<AIInsightsSummaryProps> = ({
  focusLeader,
  leaders,
  scorecards,
  period,
  freetextTopics,
  leaderMatches = true,
  dataLoading = false
}) => {
  // Same headcount source the charts use; scoped to the focus leader's directs.
  const { data: headcountPoints, loading } = useMultiLeaderHeadcount(leaders, period.startDate || null, null);

  const payload = useMemo(() => {
    // Staleness guard: the scorecards/leaders must belong to the CURRENT focus leader.
    // During a leader switch there is a one-render window where focusLeader has updated
    // but drillDownState.data still holds the previous leader's directs -- building here
    // would fire the summarizer with the wrong people. Refuse until it matches.
    if (!leaderMatches) return null;
    if (!headcountPoints || headcountPoints.length === 0 || !scorecards || scorecards.length === 0) return null;
    return buildCanaryInsightsPayload({ focusLeader, leaders, scorecards, headcountPoints, period, freetextTopics });
  }, [leaderMatches, headcountPoints, scorecards, leaders, focusLeader, period, freetextTopics]);

  // aggregatedData = the structured payload (goes to raw_data_content, guardrail-screened).
  const aggregatedData = payload ? toRawDataContent(payload) : '';
  // filters = compact cache key + human context; varies per leader/period so cache is correct.
  const filters = JSON.stringify({ leader: focusLeader.login, tier: focusLeader.tier, period: period.label, directs: leaders.length - 1 });

  // Gate the summary fetch until the payload is assembled (EnhancedAISummaryBox fetches when statusKey === statusValue).
  const statusValue = payload ? 'ready' : 'pending';

  // No payload = the focus leader has no directs/connection/headcount data to summarize (e.g. a
  // front-line manager). Do NOT mount the summarizer -- otherwise it would POST an empty
  // aggregatedData and the backend parse_request rejects it ("Missing required fields"). Render a
  // graceful state instead.
  if (!payload) {
    // Stale (leader just switched, refetch in flight) or genuinely loading -> spinner.
    // Only show the "no data" message once we're settled on the current leader with nothing to summarize.
    const isBusy = loading || dataLoading || !leaderMatches;
    return (
      <Container header={<Header variant="h2">🤖 AI Insights Summary</Header>}>
        <Box color="text-body-secondary" padding="s">
          {isBusy ? (
            <span>
              <Spinner /> Loading data...
            </span>
          ) : (
            'No connection or headcount data available to summarize for this leader and their directs.'
          )}
        </Box>
      </Container>
    );
  }

  return (
    <div className={styles.aiSummary}>
      <EnhancedAISummaryBox
        pageType={PAGE_TYPE}
        title="🤖 AI Insights Summary"
        titleCaption="AI-generated summary. It highlights patterns worth discussing but can miss context or misread small samples. Treat it as a starting point for conversation and confirm anything material against the data."
        filters={filters}
        aggregatedData={aggregatedData}
        statusKey="ready"
        statusValue={statusValue}
        showCustomAnalysis={false}
      />
    </div>
  );
};
